// قراءة البطاقة داخل نافذة الاشتراك — نفس نمط قارئ الاستقبال
// (frontend/src/features/reception/components/CardScanInput.jsx): القارئ
// يُرسل الرقم كضغطات مفاتيح تنتهي بـEnter، و`useRfidScan` يجمعها، والإدخال
// اليدوي مقبول بنفس الحقل.
//
// الفرق عن قارئ الاستقبال: هنا البطاقة **غير المسجّلة** ليست خطأً نهائياً —
// تُعرض دعوة لتسجيلها بلا مغادرة النافذة.
import { useEffect, useRef, useState } from 'react';
import { api, apiError } from '../../../shared/api.js';
import { Icon } from '../../../layout/Icon.jsx';
import { useRfidScan } from '../../../shared/useRfidScan.js';
import { formatIQD } from '../../../shared/format.js';

const UID_RX = /^[A-Za-z0-9]{4,32}$/;

export function CardReadField({
  value,
  onChange,
  /** (card | null) — البطاقة المحلولة الجاهزة للربط */
  onResolved,
  /** هل يملك المستخدم صلاحية تسجيل بطاقة جديدة؟ (reception.issue_card) */
  canIssue = false,
  autoFocus = false,
  disabled = false
}) {
  const inputRef = useRef(null);
  const [state, setState] = useState({ kind: 'idle', message: '' });
  const [adding, setAdding] = useState(false);

  useRfidScan({
    inputRef,
    onScan: (uid) => onChange?.(uid),
    autoFocus,
    autoClear: false
  });

  const uid = String(value || '').trim().toUpperCase();

  useEffect(() => {
    if (!uid) {
      setState({ kind: 'idle', message: '' });
      onResolved?.(null);
      return undefined;
    }
    if (!UID_RX.test(uid)) {
      setState({ kind: 'invalid', message: 'صيغة رقم البطاقة غير صالحة' });
      onResolved?.(null);
      return undefined;
    }

    let cancelled = false;
    setState({ kind: 'checking', message: 'جاري البحث عن البطاقة...' });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cards/by-uid/${encodeURIComponent(uid)}`);
        if (cancelled) return;
        const card = data?.card || data;
        if (!card || !card.uid) {
          setState({ kind: 'unknown', message: '' });
          onResolved?.(null);
          return;
        }
        if (card.status === 'lost' || card.status === 'blocked') {
          setState({
            kind: 'invalid',
            message: card.status === 'lost' ? 'البطاقة مسجَّلة كمفقودة' : 'البطاقة محظورة'
          });
          onResolved?.(null);
          return;
        }
        setState({ kind: 'known', message: '' });
        onResolved?.(card);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setState({ kind: 'unknown', message: '' });
        } else if (status === 403) {
          setState({ kind: 'invalid', message: 'لا صلاحية لقراءة الكروت' });
        } else {
          setState({ kind: 'invalid', message: 'تعذّر التحقق من البطاقة' });
        }
        onResolved?.(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // تسجيل البطاقة فوراً عبر مسار وحدة الكروت نفسه، فتظهر في شاشة الكروت.
  // قراءة نفس الرقم مرتين لا تُكرّر السجل: الخادم يرفض الـUID المكرر ونحن
  // نُعيد القراءة بعدها فتصير «مسجّلة».
  const registerCard = async () => {
    if (!UID_RX.test(uid) || adding) return;
    setAdding(true);
    try {
      const { data } = await api.post('/cards', { uid, status: 'available', balance: 0 });
      const card = data?.card || data;
      setState({ kind: 'known', message: 'تم تسجيل البطاقة في النظام' });
      onResolved?.(card);
    } catch (err) {
      if (err?.response?.status === 409) {
        // مسجّلة أصلاً (سبقَنا أحدهم) — نقرأها بدل إنشاء ثانٍ.
        setState({ kind: 'checking', message: 'البطاقة مسجّلة — جاري قراءتها' });
        onChange?.(uid);
      } else {
        setState({ kind: 'invalid', message: apiError(err) });
      }
    } finally {
      setAdding(false);
    }
  };

  const tone =
    state.kind === 'known' ? 'border-green text-green' :
    state.kind === 'invalid' ? 'border-red text-red' :
    state.kind === 'unknown' ? 'border-orange text-orange-deep' :
    'border-line text-ink-3';

  const pillText =
    state.kind === 'known' ? 'مسجّلة' :
    state.kind === 'unknown' ? 'غير مسجّلة' :
    state.kind === 'invalid' ? state.message :
    state.kind === 'checking' ? '...' : 'بانتظار';

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            className="input pr-9"
            value={value || ''}
            placeholder="مرّر البطاقة على القارئ أو اكتب رقمها"
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            dir="ltr"
            style={{ textAlign: 'right', fontFamily: 'monospace' }}
          />
          <Icon
            name="card"
            size={16}
            className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
          />
        </div>
        <div className={`pill border ${tone}`} style={{ whiteSpace: 'nowrap' }}>
          {state.kind === 'known' && <Icon name="check" size={12} />}
          {state.kind === 'invalid' && <Icon name="x" size={12} />}
          <span className="text-[11px]">{pillText}</span>
        </div>
        {canIssue ? (
          <button
            type="button"
            className="btn"
            style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap' }}
            onClick={registerCard}
            disabled={disabled || adding || !UID_RX.test(uid) || state.kind === 'known'}
            title={
              !UID_RX.test(uid)
                ? 'اقرأ البطاقة أو اكتب رقمها أولاً'
                : state.kind === 'known'
                  ? 'البطاقة مسجّلة أصلاً'
                  : 'تسجيل هذه البطاقة في شاشة الكروت'
            }
          >
            <Icon name="plus" size={13} /> {adding ? 'جاري الإضافة...' : 'إضافة بطاقات'}
          </button>
        ) : null}
      </div>

      {state.kind === 'checking' && state.message ? (
        <div className="text-[11.5px] text-ink-3 mt-1">{state.message}</div>
      ) : null}

      {state.kind === 'unknown' ? (
        <div
          className="rounded-[10px] px-3 py-2 text-[12.5px] mt-1.5 flex items-center justify-between gap-2 flex-wrap"
          style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
        >
          <span>هذه البطاقة غير مسجلة في النظام، هل ترغب بإضافتها؟</span>
          {canIssue ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={registerCard}
              disabled={adding}
            >
              {adding ? 'جاري الإضافة...' : 'نعم، أضفها'}
            </button>
          ) : (
            <span className="text-[11.5px]">تسجيل البطاقات يحتاج صلاحية إصدار الكروت</span>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** سطر الرصيد الابتدائي — يظهر فقط بعد اختيار بطاقة أو إضافتها. */
export function InitialBalanceField({ card, value, onChange }) {
  const current = Math.max(0, Math.round(Number(card?.balance) || 0));
  const target = value === '' || value == null ? current : Math.max(0, Math.round(Number(value) || 0));
  const diff = target - current;

  return (
    <div>
      <label className="block text-xs font-semibold text-ink-2 mb-1">الرصيد الابتدائي</label>
      <input
        className="input"
        type="number"
        min={0}
        step={250}
        value={value == null ? '' : value}
        onChange={(e) => onChange?.(e.target.value)}
        dir="ltr"
        style={{ textAlign: 'right' }}
      />
      {diff > 0 ? (
        <div className="text-[11.5px] text-ink-3 mt-1">
          رصيد البطاقة الحالي {formatIQD(current)} — سيُستلم {formatIQD(diff)} نقداً في درج الشفت
          وتُسجَّل تعبئة رصيد للبطاقة.
        </div>
      ) : diff < 0 ? (
        <div className="text-[11.5px] text-red mt-1">
          لا يمكن تخفيض الرصيد من هنا: رصيد البطاقة {formatIQD(current)}، وتخفيضه ليس عملية نقدية —
          يُعالج كتصحيح موثَّق من شاشة الكروت.
        </div>
      ) : (
        <div className="text-[11.5px] text-ink-3 mt-1">
          {current > 0
            ? `رصيد البطاقة الحالي ${formatIQD(current)} — لن يُستلم أي مبلغ إضافي`
            : 'البطاقة بلا رصيد — ارفع المبلغ إن استلمت تعبئة الآن'}
        </div>
      )}
    </div>
  );
}
